import React, { useEffect, useMemo, useState } from "react";
import { Bookmark, ChevronLeft, ChevronRight, Trash2, X } from "lucide-react";

/**
 * The New Developments sidebar, laid out like BCI's own (Christian Gravias,
 * Monday 12899126094, 23 Oct 2026: "Can we please add a sidebar like BCI").
 * Saved Search on top, then one row per filter group that opens in place. A
 * dot marks every group in use. Filters apply as they are picked and the
 * sidebar stays open ("Leave this opened").
 *
 * BCI's Contract Details, My Data, Building Material and Other groups are left
 * out: their API does not send that data.
 */

export const EMPTY_FILTERS = {
  statuses: [],
  categories: [],
  value_min: "",
  value_max: "",
  updated_days: "",
  start_from: "",
  start_to: "",
  units_min: "",
  units_max: "",
  storeys_min: "",
  storeys_max: "",
  development_types: [],
  ownership_types: [],
  roles: [],
  has_developer: false,
};

const VALUE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "500000", label: "$500K" },
  { value: "1000000", label: "$1M" },
  { value: "2000000", label: "$2M" },
  { value: "5000000", label: "$5M" },
  { value: "10000000", label: "$10M" },
  { value: "20000000", label: "$20M" },
  { value: "50000000", label: "$50M" },
  { value: "100000000", label: "$100M" },
];

const UPDATED_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 3 months" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
];

const COUNT_OPTIONS = ["", "1", "2", "3", "4", "5", "10", "20", "50", "100"];

const SELECT_CLS =
  "w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40 focus:border-[#55C5D0]";
const LABEL_CLS = "block text-[11px] font-semibold text-gray-500 mb-1";

// Which filter keys each sidebar group owns, for its "in use" dot.
const GROUP_KEYS = {
  keyword: ["q"],
  location: ["suburbs"],
  categories: ["categories"],
  stage: ["stages", "statuses"],
  value: ["value_min", "value_max"],
  dates: ["updated_days", "start_from", "start_to"],
  details: ["units_min", "units_max", "storeys_min", "storeys_max", "development_types", "ownership_types"],
  roles: ["roles", "has_developer"],
};

const inUse = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v));

const Section = ({ id, label, active, open, onToggle, children }) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <button
      type="button"
      onClick={() => onToggle(id)}
      aria-expanded={open}
      className="w-full flex items-center justify-between py-2.5 text-left text-[13px] text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0] rounded"
    >
      <span className="flex items-center gap-1.5">
        {label}
        {active && <span className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" aria-label="in use" />}
      </span>
      <ChevronRight
        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
      />
    </button>
    {open && <div className="pb-3">{children}</div>}
  </div>
);

const SHOW_FIRST = 8;

// Checkbox list with counts; long lists get "Show all" and a find box.
const CheckList = ({ name, options, selected, onToggle, empty = "Nothing to pick yet." }) => {
  const [all, setAll] = useState(false);
  const [find, setFind] = useState("");
  const rows = useMemo(() => {
    const f = find.trim().toLowerCase();
    const list = f ? options.filter((o) => (o.label || o.value).toLowerCase().includes(f)) : options;
    // Picked values stay visible even when the list is cut short.
    if (all || f) return list;
    const head = list.slice(0, SHOW_FIRST);
    const picked = list.slice(SHOW_FIRST).filter((o) => selected.includes(o.value));
    return head.concat(picked);
  }, [options, all, find, selected]);
  if (!options.length) return <p className="text-[12px] text-gray-400">{empty}</p>;
  return (
    <div>
      {options.length > 12 && (
        <input
          type="search"
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder={`Find ${name.toLowerCase()}`}
          aria-label={`Find ${name.toLowerCase()}`}
          className={`${SELECT_CLS} mb-2`}
        />
      )}
      <ul className="space-y-0.5">
        {rows.map((o) => (
          <li key={o.value}>
            <label className="flex items-center gap-2 py-1 text-[12px] text-gray-700 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => onToggle(o.value)}
                className="w-3.5 h-3.5 accent-[#55C5D0] flex-shrink-0"
              />
              <span className="flex-1 min-w-0 truncate" title={o.label || o.value}>
                {o.label || o.value}
              </span>
              {typeof o.count === "number" && (
                <span className="text-[11px] text-gray-400 tabular-nums">{o.count}</span>
              )}
            </label>
          </li>
        ))}
      </ul>
      {!find && options.length > SHOW_FIRST && (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="mt-1 text-[12px] font-semibold text-teal-600 hover:underline"
        >
          {all ? "Show fewer" : `Show all ${options.length}`}
        </button>
      )}
    </div>
  );
};

const RangeSelects = ({ label, fromKey, toKey, options, filters, setFilter, fromLabel = "From", toLabel = "To" }) => (
  <div className="grid grid-cols-2 gap-2">
    <label>
      <span className={LABEL_CLS}>{fromLabel}</span>
      <select
        aria-label={`${label} ${fromLabel.toLowerCase()}`}
        className={SELECT_CLS}
        value={filters[fromKey]}
        onChange={(e) => setFilter(fromKey, e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value || "any"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
    <label>
      <span className={LABEL_CLS}>{toLabel}</span>
      <select
        aria-label={`${label} ${toLabel.toLowerCase()}`}
        className={SELECT_CLS}
        value={filters[toKey]}
        onChange={(e) => setFilter(toKey, e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value || "any"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  </div>
);

const countOptions = COUNT_OPTIONS.map((c) => ({ value: c, label: c || "Any" }));

const DevelopmentFilterSidebar = ({
  filters,
  setFilter,
  toggleIn,
  query,
  onQuery,
  suburbs,
  onSuburbs,
  suburbOptions = [],
  stages,
  onStages,
  stageOptions = [],
  options = {},
  savedSearches = [],
  onApplySaved,
  onDeleteSaved,
  onSaveCurrent,
  canSave,
  onClearAll,
  onClose,
  mobile,
  resultCount,
}) => {
  const [open, setOpen] = useState({ saved: true });
  const [keyword, setKeyword] = useState(query || "");
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  // The search bar and saved searches change the keyword too.
  useEffect(() => setKeyword(query || ""), [query]);

  const state = { ...filters, q: query, suburbs, stages };
  const active = (group) => GROUP_KEYS[group].some((k) => inUse(state[k]));

  // One row per suburb name (Melbourne has a few with two postcodes).
  const suburbList = useMemo(() => {
    const by = {};
    suburbOptions.forEach((o) => {
      by[o.suburb] = (by[o.suburb] || 0) + (o.count || 0);
    });
    return Object.keys(by)
      .sort()
      .map((s) => ({ value: s, count: by[s] }));
  }, [suburbOptions]);

  const statusesByStage = useMemo(() => {
    const groups = {};
    (options.statuses || []).forEach((s) => {
      const k = s.stage || "Other";
      (groups[k] = groups[k] || []).push(s);
    });
    return groups;
  }, [options.statuses]);

  const submitSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    const ok = await onSaveCurrent(name.trim());
    setSaving(false);
    if (ok) {
      setName("");
      setNaming(false);
      setOpen((o) => ({ ...o, saved: true }));
    }
  };

  return (
    <div
      className={`flex flex-col min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm ${mobile ? "flex-1" : ""}`}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <h2 className="text-[13px] font-bold text-gray-900">Side Menu</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
          aria-label={mobile ? "Close filters" : "Hide side menu"}
        >
          {mobile ? <X className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
        <Section
          id="saved"
          label={
            <span className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-gray-500" /> Saved Search
            </span>
          }
          open={open.saved}
          onToggle={toggle}
        >
          {savedSearches.length === 0 ? (
            <p className="text-[12px] text-gray-400">
              No saved searches yet. Pick your filters, then save them below.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {savedSearches.map((s) => (
                <li key={s.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onApplySaved(s)}
                    className="flex-1 min-w-0 text-left py-1 text-[12px] font-semibold text-teal-700 hover:underline truncate"
                    title={`Run "${s.name}"`}
                  >
                    {s.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteSaved(s)}
                    className="p-1 text-gray-300 hover:text-red-500"
                    aria-label={`Delete saved search ${s.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <div className="flex items-center justify-between pt-4 pb-1">
          <h3 className="text-[13px] font-bold text-gray-900">Filters</h3>
          <button type="button" onClick={onClearAll} className="text-[11px] text-gray-400 hover:text-red-500">
            Clear All
          </button>
        </div>

        <Section id="keyword" label="Keyword(s)" active={active("keyword")} open={open.keyword} onToggle={toggle}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onQuery(keyword.trim());
            }}
            className="flex gap-2"
          >
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Project, address, developer"
              aria-label="Keyword"
              className={SELECT_CLS}
            />
            <button
              type="submit"
              className="px-3 rounded-lg bg-[#55C5D0] text-white text-[12px] font-semibold hover:opacity-90"
            >
              Go
            </button>
          </form>
          {query && (
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                onQuery("");
              }}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-500"
            >
              <X className="w-3 h-3" /> Clear "{query}"
            </button>
          )}
        </Section>

        <Section
          id="location"
          label="Project Location"
          active={active("location")}
          open={open.location}
          onToggle={toggle}
        >
          <CheckList
            name="Suburbs"
            options={suburbList}
            selected={suburbs}
            onToggle={(s) => onSuburbs(suburbs.includes(s) ? suburbs.filter((x) => x !== s) : [...suburbs, s])}
          />
        </Section>

        <Section
          id="categories"
          label="Categories"
          active={active("categories")}
          open={open.categories}
          onToggle={toggle}
        >
          <CheckList
            name="Categories"
            options={options.categories || []}
            selected={filters.categories}
            onToggle={(v) => toggleIn("categories", v)}
          />
        </Section>

        <Section id="stage" label="Stage & Status" active={active("stage")} open={open.stage} onToggle={toggle}>
          <span className={LABEL_CLS}>Stage</span>
          <CheckList
            name="Stages"
            options={stageOptions.map((s) => ({ value: s.stage, count: s.count }))}
            selected={stages}
            onToggle={(v) => onStages(stages.includes(v) ? stages.filter((x) => x !== v) : [...stages, v])}
          />
          {Object.keys(statusesByStage).map((stage) => (
            <div key={stage} className="mt-3">
              <span className={LABEL_CLS}>{stage} status</span>
              <CheckList
                name={`${stage} statuses`}
                options={statusesByStage[stage]}
                selected={filters.statuses}
                onToggle={(v) => toggleIn("statuses", v)}
              />
            </div>
          ))}
        </Section>

        <Section id="value" label="Project Value" active={active("value")} open={open.value} onToggle={toggle}>
          <RangeSelects
            label="Value"
            fromKey="value_min"
            toKey="value_max"
            options={VALUE_OPTIONS}
            filters={filters}
            setFilter={setFilter}
          />
        </Section>

        <Section id="dates" label="Date Range" active={active("dates")} open={open.dates} onToggle={toggle}>
          <label className="block">
            <span className={LABEL_CLS}>Updated by BCI</span>
            <select
              aria-label="Updated by BCI"
              className={SELECT_CLS}
              value={filters.updated_days}
              onChange={(e) => setFilter("updated_days", e.target.value)}
            >
              {UPDATED_OPTIONS.map((o) => (
                <option key={o.value || "any"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <span className={`${LABEL_CLS} mt-3`}>Construction start</span>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="month"
              aria-label="Construction start from"
              value={filters.start_from}
              onChange={(e) => setFilter("start_from", e.target.value)}
              className={SELECT_CLS}
            />
            <input
              type="month"
              aria-label="Construction start to"
              value={filters.start_to}
              onChange={(e) => setFilter("start_to", e.target.value)}
              className={SELECT_CLS}
            />
          </div>
        </Section>

        <Section
          id="details"
          label="Project Details"
          active={active("details")}
          open={open.details}
          onToggle={toggle}
        >
          <span className={LABEL_CLS}>Units</span>
          <RangeSelects
            label="Units"
            fromKey="units_min"
            toKey="units_max"
            options={countOptions}
            filters={filters}
            setFilter={setFilter}
            fromLabel="Min"
            toLabel="Max"
          />
          <span className={`${LABEL_CLS} mt-3`}>Storeys</span>
          <RangeSelects
            label="Storeys"
            fromKey="storeys_min"
            toKey="storeys_max"
            options={countOptions}
            filters={filters}
            setFilter={setFilter}
            fromLabel="Min"
            toLabel="Max"
          />
          <span className={`${LABEL_CLS} mt-3`}>Development type</span>
          <CheckList
            name="Development types"
            options={options.development_types || []}
            selected={filters.development_types}
            onToggle={(v) => toggleIn("development_types", v)}
            empty="Arrives with the next BCI sync."
          />
          <span className={`${LABEL_CLS} mt-3`}>Ownership type</span>
          <CheckList
            name="Ownership types"
            options={options.ownership_types || []}
            selected={filters.ownership_types}
            onToggle={(v) => toggleIn("ownership_types", v)}
            empty="Arrives with the next BCI sync."
          />
        </Section>

        <Section id="roles" label="Company Role" active={active("roles")} open={open.roles} onToggle={toggle}>
          <label className="flex items-center gap-2 py-1 text-[12px] text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_developer}
              onChange={(e) => setFilter("has_developer", e.target.checked)}
              className="w-3.5 h-3.5 accent-[#55C5D0]"
            />
            Developer listed only
          </label>
          <span className={`${LABEL_CLS} mt-2`}>Has a company in this role</span>
          <CheckList
            name="Roles"
            options={options.roles || []}
            selected={filters.roles}
            onToggle={(v) => toggleIn("roles", v)}
            empty="Arrives with the next BCI sync."
          />
        </Section>
      </div>

      <div className="border-t border-gray-100 p-3 space-y-2">
        {naming ? (
          <form onSubmit={submitSave} className="flex gap-2">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name this search"
              aria-label="Saved search name"
              maxLength={120}
              className={SELECT_CLS}
            />
            <button
              type="submit"
              disabled={!name.trim() || saving}
              className="px-3 rounded-lg bg-[#55C5D0] text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setNaming(false)}
              className="px-1 text-gray-400 hover:text-gray-700"
              aria-label="Cancel saving"
            >
              <X className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setNaming(true)}
            disabled={!canSave}
            title={canSave ? "" : "Pick a filter first"}
            className="w-full py-2 rounded-lg bg-[#55C5D0] text-white text-[12px] font-bold hover:opacity-90 disabled:opacity-40"
          >
            Add New Saved Search
          </button>
        )}
        {mobile && (
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-700"
          >
            Show {resultCount} projects
          </button>
        )}
      </div>
    </div>
  );
};

export default DevelopmentFilterSidebar;
